import { Reveal } from "@components/ui";

interface CategoryCardProps {
  icon: React.ReactNode;
  title: string;
  items: string[];
}

function CategoryCard({ icon, title, items }: CategoryCardProps) {
  return (
    <div className="group w-full rounded-[12px] md:rounded-[20px] bg-white flex flex-col items-start p-5 md:p-7 lg:p-8 gap-5 md:gap-6 border border-transparent hover:border-[#e8e9ed] hover:shadow-[0_4px_20px_rgba(0,0,0,0.08)] transition-all duration-300">
      {/* Icon */}
      <div className="w-12 h-12 md:w-14 md:h-14 rounded-[14px] bg-[#122d63]/[0.08] group-hover:bg-[#122d63] flex items-center justify-center transition-colors duration-300 text-[#122d63] group-hover:text-white">
        {icon}
      </div>

      {/* Title */}
      <h3 className="text-sm md:text-lg lg:text-[24px] tracking-[-0.04em] font-semibold text-[#1c1c1c] font-albert-sans leading-[1.2]">
        {title}
      </h3>

      {/* Bullet list */}
      <ul className="flex flex-col gap-2.5 md:gap-3">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              className="w-5 h-5 text-[#f24d2b] shrink-0 mt-0.5"
            >
              <path
                d="M9 12l2 2 4-4"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
            <span className="text-[13px] md:text-[15px] lg:text-[17px] text-[rgba(10,14,46,0.75)] font-albert-sans leading-[1.5]">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const BillingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <line x1="2" y1="10" x2="22" y2="10" />
  </svg>
);

const ServiceIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const TechIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
  </svg>
);

const EmergencyIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const ConnectionIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

const DigitalIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const categories: CategoryCardProps[] = [
  {
    icon: <BillingIcon />,
    title: "Billing & Payments",
    items: [
      "Invoice accuracy & clarity",
      "Payment processing speed",
      "Online payment experience",
      "Refund & deposit handling",
    ],
  },
  {
    icon: <ServiceIcon />,
    title: "Customer Service",
    items: [
      "Response time & professionalism",
      "Issue resolution quality",
      "Communication clarity",
      "Overall satisfaction",
    ],
  },
  {
    icon: <TechIcon />,
    title: "Technical Support",
    items: [
      "Maintenance scheduling",
      "Technician professionalism",
      "Repair quality & timeliness",
      "Equipment installation",
    ],
  },
  {
    icon: <EmergencyIcon />,
    title: "Emergency Response",
    items: [
      "Response time",
      "Safety procedures followed",
      "Communication during emergencies",
      "Post-emergency follow-up",
    ],
  },
  {
    icon: <ConnectionIcon />,
    title: "New Connections",
    items: [
      "Application process clarity",
      "Site assessment experience",
      "Installation quality",
      "Activation timeline",
    ],
  },
  {
    icon: <DigitalIcon />,
    title: "Digital Experience",
    items: [
      "Website usability",
      "Mobile app functionality",
      "Online portal features",
      "Self-service tools",
    ],
  },
];

export function ReviewCategoriesSection() {
  return (
    <section className="w-full bg-[#f9f9fb] overflow-hidden py-12 md:py-16 lg:py-[100px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col items-center gap-10 lg:gap-16">
        {/* Badge + Title */}
        <Reveal direction="up">
          <div className="flex flex-col items-center gap-4 md:gap-5">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/[0.08] text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase">
              Categories
            </span>
            <h2 className="text-center text-[24px] md:text-[40px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              What You Can Review or Rate
            </h2>
            <p className="text-center max-w-[640px] text-[14px] md:text-base lg:text-[18px] text-[rgba(10,14,46,0.75)] font-albert-sans leading-[1.6]">
              Select the service area you'd like to provide feedback on. Your input helps us improve across every touchpoint.
            </p>
          </div>
        </Reveal>

        {/* Cards Grid */}
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {categories.map((category, index) => (
            <Reveal key={index} direction="up" delay={100 + index * 80}>
              <CategoryCard {...category} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
